// src/modules/workspace/CleaningLab.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Full cleaning page — everything you'd do in pandas/Python notebook
// Operations (11): drop nulls · fill mean/median/mode/custom/ffill/bfill ·
//   remove duplicates · drop column · rename column · add computed column ·
//   search & replace · change datatype · filter rows · remove outliers ·
//   normalize/standardize · sort rows
// Live DataTable preview after every op · undo/redo history · op log
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useEffect } from 'react';
import { C, Btn, Label, SectionToggle, Sel, TxtInput, inferTypes } from './wsShared';
import DataTable from './DataTable';
import { trackAction } from '../../utils/actionStore';

// ── Tiny timestamp ────────────────────────────────────────────────────────────
const ts = () => new Date().toLocaleTimeString('en',{hour12:false});

// ── Action tracking helpers ───────────────────────────────────────────────────
const setAction = (action) => {
  if (typeof window !== 'undefined') window.__dd_current_action = action;
};
const clearAction = () => {
  if (typeof window !== 'undefined') window.__dd_current_action = null;
};

// ── Column math helpers ───────────────────────────────────────────────────────
const numVals   = (rows,col) => rows.map(r=>parseFloat(r[col])).filter(v=>!isNaN(v)&&isFinite(v));
const colMean   = (rows,col) => { const v=numVals(rows,col); return v.length?v.reduce((a,b)=>a+b,0)/v.length:0; };
const colMedian = (rows,col) => { const v=[...numVals(rows,col)].sort((a,b)=>a-b); return v.length?v[Math.floor(v.length/2)]:0; };
const colMode   = (rows,col) => {
  const counts={}; rows.forEach(r=>{ if(r[col]) counts[r[col]]=(counts[r[col]]||0)+1; });
  return Object.entries(counts).sort((a,b)=>b[1]-a[1])[0]?.[0]??'';
};

const applyRowsTypeOverrides = (rows, typeOverrides = {}) => {
  if (!rows || typeof rows !== 'object') return rows;
  const safeOverrides = { ...typeOverrides };
  try {
    Object.defineProperty(rows, '__typeOverrides', {
      value: safeOverrides,
      writable: true,
      configurable: true,
    });
  } catch {
    rows.__typeOverrides = safeOverrides;
  }
  return rows;
};

const toOverrideType = (targetType) => (
  targetType === 'numeric' || targetType === 'integer' ? 'numeric' : 'categorical'
);

// ── Export helpers ────────────────────────────────────────────────────────────
function exportCSV(rows, headers, filename) {
  const escape = v => (String(v ?? '').includes(',') || String(v ?? '').includes('"'))
    ? `"${String(v ?? '').replace(/"/g, '""')}"` : String(v ?? '');
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
    download: filename,
  });
  a.click(); URL.revokeObjectURL(a.href);
}

function exportJSON(rows, filename) {
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' })),
    download: filename,
  });
  a.click(); URL.revokeObjectURL(a.href);
}

// ─────────────────────────────────────────────────────────────────────────────
export default function CleaningLab({ ds, onUpdate }) {
  const initialTypeOverrides = ds.typeOverrides || ds.rows?.__typeOverrides || {};
  const [history, setHistory] = useState([{rows:applyRowsTypeOverrides(ds.rows, initialTypeOverrides),headers:ds.headers,label:'Initial state',typeOverrides:{...initialTypeOverrides}}]);
  const [histIdx, setHistIdx] = useState(0);
  const [opLog,   setOpLog]   = useState([{msg:'Dataset loaded.',t:'info',ts:ts()}]);
  const [tab,     setTab]     = useState('preview'); // preview | log

  // Column-op state
  const [fillCol,    setFillCol]    = useState('');
  const [fillMethod, setFillMethod] = useState('mean');
  const [fillCustom, setFillCustom] = useState('');
  const [dropCol,    setDropCol]    = useState('');
  const [renameFrom, setRenameFrom] = useState('');
  const [renameTo,   setRenameTo]   = useState('');
  const [addName,    setAddName]    = useState('');
  const [addExpr,    setAddExpr]    = useState('');
  const [srCol,      setSrCol]      = useState('');
  const [srFind,     setSrFind]     = useState('');
  const [srReplace,  setSrReplace]  = useState('');
  const [srRegex,    setSrRegex]    = useState(false);
  const [dtCol,      setDtCol]      = useState('');
  const [dtTarget,   setDtTarget]   = useState('numeric');
  const [filterCol,  setFilterCol]  = useState('');
  const [filterOp,   setFilterOp]   = useState('>');
  const [filterVal,  setFilterVal]  = useState('');
  const [outlierCol, setOutlierCol] = useState('');
  const [outlierMul, setOutlierMul] = useState(1.5);
  const [normCol,    setNormCol]    = useState('');
  const [normMethod, setNormMethod] = useState('minmax');
  const [sortCol,    setSortCol]    = useState('');
  const [sortDir,    setSortDir]    = useState('asc');

  // ── Current state ─────────────────────────────────────────────────────────
  const cur     = history[histIdx];
  const rows    = applyRowsTypeOverrides(cur.rows, cur.typeOverrides || {});
  const headers = cur.headers;
  const typeOverrides = cur.typeOverrides || {};
  const types   = useMemo(()=>inferTypes(headers,rows),[rows,headers]);
  const numCols = types.filter(t=>t.type==='numeric').map(t=>t.col);
  const allCols = headers;

  // ── Null / dupe counts for health panel ──────────────────────────────────
  const nullCount = useMemo(()=>headers.reduce((s,h)=>s+rows.filter(r=>r[h]===''||r[h]==null).length,0),[rows,headers]);
  const dupeCount = useMemo(()=>{ const seen=new Set(); let d=0; rows.forEach(r=>{ const k=JSON.stringify(r); if(seen.has(k)) d++; else seen.add(k); }); return d; },[rows]);

  // ── Push snapshot ─────────────────────────────────────────────────────────
  function apply(newRows, newHeaders, label, nextTypeOverrides = typeOverrides) {
    const nextHeaders = newHeaders ?? headers;
    const prunedOverrides = Object.fromEntries(
      Object.entries(nextTypeOverrides || {}).filter(([col]) => nextHeaders.includes(col))
    );
    const rowsWithOverrides = applyRowsTypeOverrides(newRows, prunedOverrides);
    const snap={rows:rowsWithOverrides, headers:nextHeaders, label, typeOverrides:prunedOverrides};
    const base=history.slice(0,histIdx+1);
    const next=[...base,snap];
    setHistory(next); setHistIdx(next.length-1);
    setOpLog(p=>[...p.slice(-120),{msg:label,t:'ok',ts:ts()}]);
    if (typeof window !== 'undefined' && window.__dd_current_action) {
      trackAction(window.__dd_current_action);
      window.__dd_current_action = null;
    }
    onUpdate({...ds,rows:rowsWithOverrides,headers:nextHeaders,typeOverrides:prunedOverrides,types:inferTypes(nextHeaders,rowsWithOverrides)});
  }
  function addLog(msg,t='info'){ setOpLog(p=>[...p.slice(-120),{msg,t,ts:ts()}]); }
  function undo(){ if(histIdx<=0) return; const p=history[histIdx-1]; const rowsWithOverrides=applyRowsTypeOverrides(p.rows,p.typeOverrides||{}); setHistIdx(histIdx-1); addLog(`↩ Undid: ${history[histIdx].label}`,'warn'); onUpdate({...ds,rows:rowsWithOverrides,headers:p.headers,typeOverrides:p.typeOverrides||{},types:inferTypes(p.headers,rowsWithOverrides)}); }
  function redo(){ if(histIdx>=history.length-1) return; const n=history[histIdx+1]; const rowsWithOverrides=applyRowsTypeOverrides(n.rows,n.typeOverrides||{}); setHistIdx(histIdx+1); addLog(`↪ Redid: ${n.label}`,'ok'); onUpdate({...ds,rows:rowsWithOverrides,headers:n.headers,typeOverrides:n.typeOverrides||{},types:inferTypes(n.headers,rowsWithOverrides)}); }
  function reset(){ const o=history[0]; const rowsWithOverrides=applyRowsTypeOverrides(o.rows,o.typeOverrides||{}); setHistIdx(0); addLog('Reset to original state','warn'); onUpdate({...ds,rows:rowsWithOverrides,headers:o.headers,typeOverrides:o.typeOverrides||{},types:inferTypes(o.headers,rowsWithOverrides)}); }

  // ─────────────────────────────────────────────────────────────────────────
  // OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────

  const ops = {
    dropNullRows() {
      setAction({
        module: 'auto_eda',
        type: 'clean:drop_null_rows',
        label: 'Drop null rows',
        params: {},
        replayable: true,
      });
      const before=rows.length;
      const r=rows.filter(row=>headers.every(h=>row[h]!==''&&row[h]!=null));
      apply(r,headers,`Drop null rows — removed ${before-r.length}, ${r.length} remain`);
    },
    dropNullCols() {
      setAction({
        module: 'auto_eda',
        type: 'clean:drop_null_cols',
        label: 'Drop null columns (>50%)',
        params: { threshold: 0.5 },
        replayable: true,
      });
      const keep=headers.filter(h=>rows.filter(r=>r[h]===''||r[h]==null).length/rows.length<0.5);
      const dropped=headers.filter(h=>!keep.includes(h));
      if(!dropped.length){ clearAction(); addLog('No columns exceed 50% null threshold','info');return;}
      const r=rows.map(row=>{ const o={}; keep.forEach(h=>{o[h]=row[h];}); return o; });
      apply(r,keep,`Drop high-null cols (>50%): ${dropped.join(', ')}`);
    },
    fillNull() {
      setAction({
        module: 'auto_eda',
        type: 'clean:fill_nulls',
        label: `Fill nulls in "${fillCol || 'column'}"`,
        params: { col: fillCol, method: fillMethod },
        replayable: true,
      });
      if(!fillCol){ clearAction(); addLog('Select a column','err');return;}
      let r,label;
      if(fillMethod==='mean'){
        const v=+colMean(rows,fillCol).toFixed(4);
        r=rows.map(row=>({...row,[fillCol]:(row[fillCol]===''||row[fillCol]==null)?String(v):row[fillCol]}));
        label=`Fill "${fillCol}" nulls → mean (${v})`;
      } else if(fillMethod==='median'){
        const v=+colMedian(rows,fillCol).toFixed(4);
        r=rows.map(row=>({...row,[fillCol]:(row[fillCol]===''||row[fillCol]==null)?String(v):row[fillCol]}));
        label=`Fill "${fillCol}" nulls → median (${v})`;
      } else if(fillMethod==='mode'){
        const v=colMode(rows,fillCol);
        r=rows.map(row=>({...row,[fillCol]:(!row[fillCol])?v:row[fillCol]}));
        label=`Fill "${fillCol}" nulls → mode ("${v}")`;
      } else if(fillMethod==='custom'){
        r=rows.map(row=>({...row,[fillCol]:(row[fillCol]===''||row[fillCol]==null)?fillCustom:row[fillCol]}));
        label=`Fill "${fillCol}" nulls → "${fillCustom}"`;
      } else if(fillMethod==='ffill'){
        r=[...rows]; let last='';
        for(let i=0;i<r.length;i++){if(r[i][fillCol]!==''&&r[i][fillCol]!=null) last=r[i][fillCol]; else r[i]={...r[i],[fillCol]:last};}
        label=`Forward-fill "${fillCol}"`;
      } else if(fillMethod==='bfill'){
        r=[...rows]; let last='';
        for(let i=r.length-1;i>=0;i--){if(r[i][fillCol]!==''&&r[i][fillCol]!=null) last=r[i][fillCol]; else r[i]={...r[i],[fillCol]:last};}
        label=`Backward-fill "${fillCol}"`;
      }
      apply(r,headers,label);
    },
    removeDuplicates() {
      setAction({
        module: 'auto_eda',
        type: 'clean:remove_duplicates',
        label: 'Remove duplicates',
        params: {},
        replayable: true,
      });
      const seen=new Set(); const before=rows.length;
      const r=rows.filter(row=>{ const k=JSON.stringify(row); if(seen.has(k)) return false; seen.add(k); return true; });
      apply(r,headers,`Remove duplicates — removed ${before-r.length}`);
    },
    dropColumn() {
      setAction({
        module: 'auto_eda',
        type: 'clean:drop_column',
        label: `Drop column "${dropCol || ''}"`,
        params: { col: dropCol },
        replayable: true,
      });
      if(!dropCol){ clearAction(); addLog('Select a column','err');return;}
      const h=headers.filter(x=>x!==dropCol);
      const r=rows.map(row=>{ const o={...row}; delete o[dropCol]; return o; });
      const nextOverrides = { ...typeOverrides };
      delete nextOverrides[dropCol];
      apply(r,h,`Drop column "${dropCol}"`,nextOverrides); setDropCol('');
    },
    renameColumn() {
      setAction({
        module: 'auto_eda',
        type: 'clean:rename_column',
        label: `Rename "${renameFrom || ''}" → "${renameTo.trim()}"`,
        params: { from: renameFrom, to: renameTo.trim() },
        replayable: true,
      });
      if(!renameFrom||!renameTo.trim()){ clearAction(); addLog('Fill both fields','err');return;}
      if(headers.includes(renameTo.trim())){ clearAction(); addLog('Name already exists','err');return;}
      const newName=renameTo.trim();
      const h=headers.map(x=>x===renameFrom?newName:x);
      const r=rows.map(row=>{ const o={}; Object.entries(row).forEach(([k,v])=>{o[k===renameFrom?newName:k]=v;}); return o; });
      const nextOverrides = { ...typeOverrides };
      if (nextOverrides[renameFrom]) {
        nextOverrides[newName] = nextOverrides[renameFrom];
        delete nextOverrides[renameFrom];
      }
      apply(r,h,`Rename "${renameFrom}" → "${newName}"`,nextOverrides); setRenameFrom(''); setRenameTo('');
    },
    addColumn() {
      const name=addName.trim();
      setAction({
        module: 'auto_eda',
        type: 'clean:add_column',
        label: `Add column "${name}"`,
        params: { name, expr: addExpr },
        replayable: true,
      });
      if(!name){ clearAction(); addLog('Enter column name','err');return;}
      if(headers.includes(name)){ clearAction(); addLog('Column already exists','err');return;}
      try {
        const r=rows.map((row,_idx)=>{
          const ctx={}; headers.forEach(h=>{ const v=parseFloat(row[h]); ctx[h.replace(/[^a-zA-Z0-9_]/g,'_')]=isNaN(v)?row[h]:v; });
          ctx._idx=_idx; ctx._len=rows.length;
          let expr=addExpr; headers.forEach(h=>{ expr=expr.replaceAll(h,h.replace(/[^a-zA-Z0-9_]/g,'_')); });
          const fn=new Function(...Object.keys(ctx),`return (${expr})`);
          return {...row,[name]:fn(...Object.values(ctx))??''};
        });
        apply(r,[...headers,name],`Add column "${name}" = ${addExpr}`); setAddName(''); setAddExpr('');
      } catch(e){ clearAction(); addLog(`Expression error: ${e.message}`,'err'); }
    },
    searchReplace() {
      setAction({
        module: 'auto_eda',
        type: 'clean:search_replace',
        label: `Search/replace in "${srCol || ''}"`,
        params: { col: srCol, find: srFind, replace: srReplace },
        replayable: true,
      });
      if(!srCol||!srFind){ clearAction(); addLog('Select column + enter search term','err');return;}
      let count=0;
      const r=rows.map(row=>{
        const v=String(row[srCol]??'');
        let nv;
        if(srRegex){ try{nv=v.replace(new RegExp(srFind,'g'),srReplace);}catch{nv=v;} } else nv=v.replaceAll(srFind,srReplace);
        if(nv!==v) count++;
        return {...row,[srCol]:nv};
      });
      apply(r,headers,`Search/replace in "${srCol}": "${srFind}" → "${srReplace}" (${count} changed)`); setSrFind(''); setSrReplace('');
    },
    changeDatatype() {
      setAction({
        module: 'auto_eda',
        type: 'clean:cast_column',
        label: `Cast "${dtCol || ''}" → ${dtTarget}`,
        params: { col: dtCol, type: dtTarget },
        replayable: true,
      });
      if(!dtCol){ clearAction(); addLog('Select a column','err');return;}
      const DT_MAP = {
        numeric:   v=>{ const n=parseFloat(v); return isNaN(n)?'':String(n); },
        integer:   v=>{ const n=parseInt(v,10); return isNaN(n)?'':String(n); },
        string:    v=>String(v??''),
        boolean:   v=>['1','true','yes','y'].includes(String(v).toLowerCase())?'true':'false',
        lowercase: v=>String(v??'').toLowerCase(),
        uppercase: v=>String(v??'').toUpperCase(),
        trim:      v=>String(v??'').trim(),
      };
      const fn=DT_MAP[dtTarget]||DT_MAP.string;
      const r=rows.map(row=>({...row,[dtCol]:fn(row[dtCol])}));
      const nextOverrides = { ...typeOverrides, [dtCol]: toOverrideType(dtTarget) };
      apply(r,headers,`Cast "${dtCol}" → ${dtTarget}`,nextOverrides);
    },
    filterRows() {
      setAction({
        module: 'auto_eda',
        type: 'clean:filter_rows',
        label: `Filter rows on "${filterCol || ''}"`,
        params: { col: filterCol, op: filterOp, val: filterVal },
        replayable: true,
      });
      if(!filterCol||filterVal===''){ clearAction(); addLog('Fill all filter fields','err');return;}
      const isNum=numCols.includes(filterCol); const before=rows.length;
      const r=rows.filter(row=>{
        const cv=row[filterCol]??''; const cell=isNum?parseFloat(cv):String(cv).toLowerCase(); const fv=isNum?parseFloat(filterVal):filterVal.toLowerCase();
        switch(filterOp){
          case '>':        return cell>fv;
          case '>=':       return cell>=fv;
          case '<':        return cell<fv;
          case '<=':       return cell<=fv;
          case '==':       return String(cv)===String(filterVal);
          case '!=':       return String(cv)!==String(filterVal);
          case 'contains': return String(cv).toLowerCase().includes(filterVal.toLowerCase());
          case 'starts':   return String(cv).toLowerCase().startsWith(filterVal.toLowerCase());
          case 'ends':     return String(cv).toLowerCase().endsWith(filterVal.toLowerCase());
          default:         return true;
        }
      });
      apply(r,headers,`Filter "${filterCol}" ${filterOp} "${filterVal}" — kept ${r.length} of ${before}`); setFilterVal('');
    },
    removeOutliers() {
      setAction({
        module: 'auto_eda',
        type: 'clean:remove_outliers',
        label: `Remove outliers in "${outlierCol || ''}"`,
        params: { col: outlierCol, multiplier: outlierMul },
        replayable: true,
      });
      if(!outlierCol){ clearAction(); addLog('Select a column','err');return;}
      const vals=[...numVals(rows,outlierCol)].sort((a,b)=>a-b);
      const q1=vals[Math.floor(vals.length*.25)],q3=vals[Math.floor(vals.length*.75)],iqr=q3-q1;
      const lo=q1-outlierMul*iqr, hi=q3+outlierMul*iqr; const before=rows.length;
      const r=rows.filter(row=>{ const v=parseFloat(row[outlierCol]); return isNaN(v)||(v>=lo&&v<=hi); });
      apply(r,headers,`Remove outliers "${outlierCol}" IQR×${outlierMul} [${lo.toFixed(2)},${hi.toFixed(2)}] — removed ${before-r.length}`);
    },
    normalize() {
      setAction({
        module: 'auto_eda',
        type: 'clean:normalize',
        label: `Normalize "${normCol || ''}"`,
        params: { col: normCol, method: normMethod },
        replayable: true,
      });
      if(!normCol){ clearAction(); addLog('Select a column','err');return;}
      let r,label;
      if(normMethod==='minmax'){
        const vals=numVals(rows,normCol); const mn=Math.min(...vals),mx=Math.max(...vals),range=mx-mn||1;
        r=rows.map(row=>{ const v=parseFloat(row[normCol]); return {...row,[normCol]:isNaN(v)?row[normCol]:+((v-mn)/range).toFixed(6)}; });
        label=`Min-max normalize "${normCol}" → [0,1]`;
      } else {
        const vals=numVals(rows,normCol); const mean=vals.reduce((a,b)=>a+b,0)/vals.length; const std=Math.sqrt(vals.reduce((a,v)=>a+(v-mean)**2,0)/vals.length)||1;
        r=rows.map(row=>{ const v=parseFloat(row[normCol]); return {...row,[normCol]:isNaN(v)?row[normCol]:+((v-mean)/std).toFixed(6)}; });
        label=`Z-score standardize "${normCol}"`;
      }
      apply(r,headers,label);
    },
    sortRows() {
      setAction({
        module: 'auto_eda',
        type: 'clean:sort_rows',
        label: `Sort rows by "${sortCol || ''}"`,
        params: { col: sortCol, dir: sortDir },
        replayable: true,
      });
      if(!sortCol){ clearAction(); addLog('Select a column','err');return;}
      const isNum=numCols.includes(sortCol);
      const r=[...rows].sort((a,b)=>{
        const av=a[sortCol]??'',bv=b[sortCol]??'';
        const cmp=isNum?parseFloat(av)-parseFloat(bv):String(av).localeCompare(String(bv));
        return sortDir==='asc'?cmp:-cmp;
      });
      apply(r,headers,`Sort by "${sortCol}" ${sortDir.toUpperCase()}`);
    },
  };

  useEffect(() => {
    const handler = (e) => {
      const { op, params = {} } = e.detail || {};
      if (!op || !ops[op]) return;

      if (params.col) {
        setFillCol(params.col);
        setDropCol(params.col);
        setSrCol(params.col);
        setDtCol(params.col);
        setFilterCol(params.col);
        setOutlierCol(params.col);
        setNormCol(params.col);
        setSortCol(params.col);
      }
      if (op === 'fillNull') {
        if (params.method) setFillMethod(params.method);
      }
      if (op === 'renameColumn') {
        if (params.from) setRenameFrom(params.from);
        if (params.to) setRenameTo(params.to);
      }
      if (op === 'addColumn') {
        if (params.name) setAddName(params.name);
        if (params.expr) setAddExpr(params.expr);
      }
      if (op === 'searchReplace') {
        if (params.find) setSrFind(params.find);
        if (params.replace) setSrReplace(params.replace);
      }
      if (op === 'changeDatatype') {
        if (params.type) setDtTarget(params.type);
      }
      if (op === 'filterRows') {
        if (params.op) setFilterOp(params.op);
        if (params.val !== undefined) setFilterVal(String(params.val));
      }
      if (op === 'removeOutliers') {
        if (params.multiplier) setOutlierMul(params.multiplier);
      }
      if (op === 'normalize') {
        if (params.method) setNormMethod(params.method);
      }
      if (op === 'sortRows') {
        if (params.dir) setSortDir(params.dir);
      }

      setTimeout(() => ops[op]?.(), 50);
    };
    window.addEventListener('dd:replay:clean', handler);
    return () => window.removeEventListener('dd:replay:clean', handler);
  }, [ops]);

  const FILL_OPTS=['mean','median','mode','custom','ffill','bfill'];
  const DT_OPTS  =['numeric','integer','string','boolean','lowercase','uppercase','trim'];
  const FILTER_OPS=['>','>=','<','<=','==','!=','contains','starts','ends'];

  return (
    <div style={{ display:'flex',gap:14,minHeight:0 }}>

      {/* ── LEFT — table + log ─────────────────────────────────────── */}
      <div style={{ display:'flex',flexDirection:'column',gap:10,minHeight:0,overflow:'hidden',flex:1,minWidth:0,order:2 }}>

        {/* Controls row */}
        <div style={{ display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',flexShrink:0 }}>
          <div style={{ display:'flex',gap:2,background:C.deep,border:`1px solid ${C.b0}`,borderRadius:3,padding:2 }}>
            {['preview','log'].map(t=>(
              <button key={t} onClick={()=>setTab(t)} style={{
                padding:'4px 12px',border:'none',borderRadius:2,cursor:'pointer',
                background:tab===t?C.cyan:'transparent',color:tab===t?C.void:C.t3,
                fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,textTransform:'uppercase',
              }}>{t==='preview'?'DATA PREVIEW':'OPERATION LOG'}</button>
            ))}
          </div>
          <span style={{ fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace' }}>{rows.length.toLocaleString()} rows · {headers.length} cols</span>
          <div style={{ marginLeft:'auto',display:'flex',gap:6,flexWrap:'wrap' }}>
            <Btn small onClick={undo}  disabled={histIdx<=0}                color={C.gold}>↩ UNDO</Btn>
            <Btn small onClick={redo}  disabled={histIdx>=history.length-1} color={C.cyan}>↪ REDO</Btn>
            <Btn small onClick={reset}                                       color={C.red}>RESET</Btn>
            <div style={{ width:1,background:C.b1,alignSelf:'stretch' }}/>
            <Btn small onClick={()=>exportCSV(rows,headers,`cleaned_${ds.name||'data'}`)} color={C.green}>↓ CSV</Btn>
            <Btn small onClick={()=>exportJSON(rows,`cleaned_${(ds.name||'data').replace('.csv','.json')}`)} color={C.violet}>↓ JSON</Btn>
          </div>
        </div>

        {/* Version breadcrumb */}
        <div style={{ display:'flex',gap:3,overflowX:'auto',paddingBottom:2,flexShrink:0 }}>
          {history.map((h,i)=>(
            <button key={i} onClick={()=>{ const rowsWithOverrides=applyRowsTypeOverrides(h.rows,h.typeOverrides||{}); setHistIdx(i); onUpdate({...ds,rows:rowsWithOverrides,headers:h.headers,typeOverrides:h.typeOverrides||{},types:inferTypes(h.headers,rowsWithOverrides)}); }}
              title={h.label}
              style={{
                padding:'2px 8px',border:`1px solid ${i===histIdx?C.cyan:C.b1}`,
                background:i===histIdx?C.cyan+'20':'transparent',color:i===histIdx?C.cyan:C.t4,
                fontFamily:'JetBrains Mono,monospace',fontSize:8,borderRadius:2,cursor:'pointer',whiteSpace:'nowrap',flexShrink:0,
              }}>{i===0?'v0 orig':`v${i}`}</button>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex:1,minHeight:0,overflowY:'auto' }}>
          {tab==='preview'
            ? <DataTable rows={rows} headers={headers} types={types} showControls />
            : (
              <div style={{ background:C.deep,border:`1px solid ${C.b0}`,borderRadius:3,padding:'10px 14px',fontFamily:'JetBrains Mono,monospace',fontSize:11,minHeight:200,maxHeight:600,overflowY:'auto' }}>
                {opLog.map((l,i)=>(
                  <div key={i} style={{ color:l.t==='ok'?C.green:l.t==='err'?C.red:l.t==='warn'?C.gold:C.t3,marginBottom:3 }}>
                    <span style={{ color:C.t4 }}>[{l.ts}]</span> {l.msg}
                  </div>
                ))}
                <span style={{ color:C.cyan,animation:'blink 1s infinite' }}>█</span>
              </div>
            )
          }
        </div>
      </div>

      {/* ── RIGHT — operation panels ────────────────────────────────── */}
      <div style={{ overflowY:'auto',display:'flex',flexDirection:'column',gap:8,flex:'0 0 300px',width:300,order:1 }}>

        

        {/* 1. Missing values */}
        <SectionToggle title="1 · HANDLE MISSING VALUES" accent={C.red} defaultOpen={false}>
          <div style={{ display:'flex',gap:6,marginBottom:10,flexWrap:'wrap' }}>
            <Btn small onClick={ops.dropNullRows} color={C.red}>DROP NULL ROWS</Btn>
            <Btn small onClick={ops.dropNullCols} color={C.orange}>DROP NULL COLS &gt;50%</Btn>
          </div>
          <Label>FILL A SPECIFIC COLUMN</Label>
          <div style={{ display:'flex',gap:6,marginBottom:6,flexWrap:'wrap' }}>
            <Sel value={fillCol} onChange={setFillCol} style={{flex:1,minWidth:80}}
              options={[{value:'',label:'— col —'},...allCols.map(c=>({value:c,label:c}))]} />
            <Sel value={fillMethod} onChange={setFillMethod}
              options={FILL_OPTS.map(m=>({value:m,label:m}))} />
          </div>
          {fillMethod==='custom'&&<TxtInput value={fillCustom} onChange={setFillCustom} placeholder="Custom value" style={{width:'100%',marginBottom:6}} />}
          <Btn small onClick={ops.fillNull} disabled={!fillCol} color={C.gold}>FILL NULLS</Btn>
        </SectionToggle>

        {/* 2. Duplicates */}
        <SectionToggle title="2 · REMOVE DUPLICATES" accent={C.orange} defaultOpen={false}>
          <div style={{ fontSize:11,color:C.t3,marginBottom:8 }}>
            {dupeCount>0?<span style={{color:C.gold}}>⚠ {dupeCount} duplicate rows found</span>:'No duplicates detected'}
          </div>
          <Btn small onClick={ops.removeDuplicates} color={C.orange}>REMOVE DUPLICATES</Btn>
        </SectionToggle>

        {/* 3. Drop column */}
        <SectionToggle title="3 · DROP COLUMN" accent={C.red} defaultOpen={false}>
          <div style={{ display:'flex',gap:6 }}>
            <Sel value={dropCol} onChange={setDropCol} style={{flex:1}}
              options={[{value:'',label:'— column —'},...allCols.map(c=>({value:c,label:c}))]} />
            <Btn small onClick={ops.dropColumn} disabled={!dropCol} color={C.red}>DROP</Btn>
          </div>
        </SectionToggle>

        {/* 4. Rename column */}
        <SectionToggle title="4 · RENAME COLUMN" accent={C.cyan} defaultOpen={false}>
          <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
            <Sel value={renameFrom} onChange={setRenameFrom} options={[{value:'',label:'— column —'},...allCols.map(c=>({value:c,label:c}))]} />
            <TxtInput value={renameTo} onChange={setRenameTo} placeholder="New name" style={{width:'100%'}} />
            <Btn small onClick={ops.renameColumn} disabled={!renameFrom||!renameTo.trim()} color={C.cyan}>RENAME</Btn>
          </div>
        </SectionToggle>

        {/* 5. Add computed column */}
        <SectionToggle title="5 · ADD COMPUTED COLUMN" accent={C.green} defaultOpen={false}>
          <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
            <TxtInput value={addName} onChange={setAddName} placeholder="New column name" style={{width:'100%'}} />
            <TxtInput value={addExpr} onChange={setAddExpr} placeholder={`e.g. ${headers[0]||'col1'} * 2`} style={{width:'100%'}} />
            <div style={{ fontSize:9,color:C.t4,lineHeight:1.6 }}>Use column names, +−×÷, Math.log(), Math.sqrt(), _idx, _len</div>
            <Btn small onClick={ops.addColumn} disabled={!addName.trim()||!addExpr.trim()} color={C.green}>ADD COLUMN</Btn>
          </div>
        </SectionToggle>

        {/* 6. Search & Replace */}
        <SectionToggle title="6 · SEARCH & REPLACE" accent={C.violet} defaultOpen={false}>
          <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
            <Sel value={srCol} onChange={setSrCol} options={[{value:'',label:'— column —'},...allCols.map(c=>({value:c,label:c}))]} />
            <TxtInput value={srFind}    onChange={setSrFind}    placeholder="Find…"    style={{width:'100%'}} />
            <TxtInput value={srReplace} onChange={setSrReplace} placeholder="Replace with…" style={{width:'100%'}} />
            <label style={{ display:'flex',alignItems:'center',gap:6,fontSize:10,color:C.t3,cursor:'pointer' }}>
              <input type="checkbox" checked={srRegex} onChange={e=>setSrRegex(e.target.checked)} />
              Use RegEx
            </label>
            <Btn small onClick={ops.searchReplace} disabled={!srCol||!srFind} color={C.violet}>APPLY</Btn>
          </div>
        </SectionToggle>

        {/* 7. Change datatype */}
        <SectionToggle title="7 · CHANGE DATATYPE" accent={C.cyan} defaultOpen={false}>
          <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
            <Sel value={dtCol} onChange={setDtCol} style={{flex:1}}
              options={[{value:'',label:'— column —'},...allCols.map(c=>({value:c,label:c}))]} />
            <Sel value={dtTarget} onChange={setDtTarget} options={DT_OPTS.map(d=>({value:d,label:d}))} />
            <Btn small onClick={ops.changeDatatype} disabled={!dtCol} color={C.cyan}>CAST</Btn>
          </div>
        </SectionToggle>

        {/* 8. Filter rows */}
        <SectionToggle title="8 · FILTER ROWS" accent={C.gold} defaultOpen={false}>
          <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
            <Sel value={filterCol} onChange={setFilterCol} options={[{value:'',label:'— column —'},...allCols.map(c=>({value:c,label:c}))]} />
            <div style={{ display:'flex',gap:6 }}>
              <Sel value={filterOp} onChange={setFilterOp} options={FILTER_OPS.map(o=>({value:o,label:o}))} />
              <TxtInput value={filterVal} onChange={setFilterVal} placeholder="value" style={{flex:1}} />
            </div>
            <Btn small onClick={ops.filterRows} disabled={!filterCol||filterVal===''} color={C.gold}>APPLY FILTER</Btn>
          </div>
        </SectionToggle>

        {/* 9. Remove outliers */}
        <SectionToggle title="9 · REMOVE OUTLIERS (IQR)" accent={C.orange} defaultOpen={false}>
          <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
            <Sel value={outlierCol} onChange={setOutlierCol} options={[{value:'',label:'— numeric col —'},...numCols.map(c=>({value:c,label:c}))]} />
            <Label>IQR MULTIPLIER — {outlierMul}</Label>
            <input type="range" min={0.5} max={3} step={0.1} value={outlierMul} onChange={e=>setOutlierMul(+e.target.value)} style={{accentColor:C.orange}} />
            <Btn small onClick={ops.removeOutliers} disabled={!outlierCol} color={C.orange}>REMOVE</Btn>
          </div>
        </SectionToggle>

        {/* 10. Normalize / standardize */}
        <SectionToggle title="10 · NORMALIZE / STANDARDIZE" accent={C.violet} defaultOpen={false}>
          <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
            <Sel value={normCol} onChange={setNormCol} options={[{value:'',label:'— numeric col —'},...numCols.map(c=>({value:c,label:c}))]} />
            <div style={{ display:'flex',gap:4 }}>
              {['minmax','zscore'].map(m=>(
                <button key={m} onClick={()=>setNormMethod(m)} style={{
                  padding:'4px 9px',border:`1px solid ${normMethod===m?C.violet:C.b1}`,
                  background:normMethod===m?C.violet+'20':'transparent',color:normMethod===m?C.violet:C.t3,
                  fontFamily:'JetBrains Mono,monospace',fontSize:8,borderRadius:2,cursor:'pointer',
                }}>{m==='minmax'?'MIN-MAX [0,1]':'Z-SCORE'}</button>
              ))}
            </div>
            <Btn small onClick={ops.normalize} disabled={!normCol} color={C.violet}>APPLY</Btn>
          </div>
        </SectionToggle>

        {/* 11. Sort */}
        <SectionToggle title="11 · SORT ROWS" accent={C.cyan} defaultOpen={false}>
          <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
            <Sel value={sortCol} onChange={setSortCol} style={{flex:1}} options={[{value:'',label:'— column —'},...allCols.map(c=>({value:c,label:c}))]} />
            <div style={{ display:'flex',gap:3 }}>
              {['asc','desc'].map(d=>(
                <button key={d} onClick={()=>setSortDir(d)} style={{
                  padding:'4px 9px',border:`1px solid ${sortDir===d?C.cyan:C.b1}`,
                  background:sortDir===d?C.cyan+'20':'transparent',color:sortDir===d?C.cyan:C.t3,
                  fontFamily:'JetBrains Mono,monospace',fontSize:8,borderRadius:2,cursor:'pointer',
                }}>{d==='asc'?'↑ ASC':'↓ DESC'}</button>
              ))}
            </div>
            <Btn small onClick={ops.sortRows} disabled={!sortCol} color={C.cyan}>SORT</Btn>
          </div>
        </SectionToggle>

      </div>
    </div>
  );
}
